'use client';

import * as React from 'react';
import { Matcher } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { formatISODate } from '@/utils/DateFunctions';
import { add, format, getYear, isValid, parseISO, sub } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';

import { Button } from './button';
import { Calendar, CalendarProps } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type DatePickerProps = {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  disabled?: boolean;
  name?: string;
  disabledDates?: Matcher | Matcher[];
  className?: string;
  variant?: 'outline' | 'underline';
} & Pick<CalendarProps, 'defaultMonth' | 'onDayClick'>;

export const DatePicker = React.forwardRef(
  (
    { onChange, value, disabled, name, disabledDates, className, variant = 'outline', ...props }: DatePickerProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const [open, setOpen] = React.useState(false);
    const { i18n, t } = useTranslation();

    const date = value || undefined;
    const parsedDate = date && isValid(parseISO(date)) ? parseISO(date) : undefined;

    const handleChangeValue = (newDate: Date) => {
      onChange?.({
        target: {
          value: newDate ? format(newDate, 'yyyy-MM-dd') : '',
          name,
        },
      } as React.ChangeEvent<HTMLInputElement>);
      setOpen(false);
    };

    const locale = (() => {
      switch (i18n.language) {
        case 'en-US':
          return enUS;
        default:
          return ptBR;
      }
    })();

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <input className="absolute h-0 w-0" name={name} value={date} tabIndex={-1} ref={ref} />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={variant === 'underline' ? 'ghost' : 'outline'}
            className={cn(
              'w-[280px] justify-start text-left font-normal',
              variant === 'underline' &&
                '!shadow-none !rounded-none !border-0 !border-b-[3px] !border-black !p-2 !text-primary hover:!border-primary/40 disabled:!text-black',
              !date && 'text-muted-foreground',
              className,
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {parsedDate ? formatISODate(parsedDate, 'PPP', locale) : <span>{t('pickDate')}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-min p-0">
          <Calendar
            locale={locale}
            selected={parsedDate}
            initialFocus
            disabled={disabledDates ?? []}
            onSelect={(date: any) => handleChangeValue(date)}
            fromYear={getYear(sub(new Date(), { years: 200 }))}
            toYear={getYear(add(new Date(), { years: 100 }))}
            {...props}
            mode="single"
            fixedWeeks
            captionLayout="dropdown-buttons"
          />
        </PopoverContent>
      </Popover>
    );
  },
);
